// /models — the project's MODELS: custom ML trained on its governed data.
//
// Deliberately NOT shaped like /agents. An agent is non-deterministic and
// self-directing, so that page is a roster you open and talk to. A model is
// deterministic and versioned, so this page is built around the four things
// that make a model trustworthy and an agent doesn't have:
//
//   1. VERSIONS      — an immutable history; promoting one is a single click,
//                      and rolling back is the same click on an older row.
//   2. METRICS       — with the CROSS-VALIDATED figure shown as the headline,
//                      because a single holdout on a small dataset flatters.
//   3. LINEAGE       — the Dolt commit whose governed data trained the version.
//                      This is the question cortex can answer that a generic
//                      model host cannot.
//   4. IMPORTANCES   — which features drive the score. A number with no
//                      explanation gets ignored by whoever has to act on it,
//                      and a dominant feature is how you spot leakage.
//
// Slice 1 is batch scoring only (docs/ml-models-surface.md §8): there is no
// interactive workflow step yet, so the page trains, explains and promotes.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain, Loader2, RefreshCw, Plus, Globe, Lock, GitCommitHorizontal,
  TrendingUp, Play, CheckCircle2, AlertTriangle, X, Trash2,
  Database, Target, Layers, Sparkles, Wrench, ClipboardList, MoreVertical,
  PanelLeftClose, PanelLeftOpen, Share2, FlaskConical, Cpu, MessageCircle,
  Pencil, ExternalLink,
} from "lucide-react";
import {
  listModels, getModel, createModel, updateModelVisibility, removeModel,
  promoteVersion, trainModel, scoreBatch, updateModelDescription, getModelManifest,
} from "../services/models";
import { ModelRunner } from "./ModelRun";
import { listAppRequests } from "../services/apps";
import { REQUEST_KIND_META } from "@/components/board/requestMeta";
import AppRequestDialog from "../components/AppRequestDialog";
import VersionHistory from "../components/VersionHistory";
import { createPageUrl } from "@/utils";
import { useProject } from "@/contexts/ProjectContext";
import { isPortal } from "../utils/portal";

// Model requests ride the SAME lifecycle as app and agent requests: filed to
// /api/app_requests, worked by the fleet, tracked on the Board.
const MODEL_REQUEST_KINDS = [
  { kind: "new_model", label: "New model", icon: Sparkles, hint: REQUEST_KIND_META.new_model.hint,
    titlePlaceholder: "What should the model predict?",
    detailsPlaceholder: "Describe it in plain language — what outcome to predict, which governed tables hold the signal, and how the prediction will be used…" },
  { kind: "change_model", label: "Change a model", icon: Wrench, hint: REQUEST_KIND_META.change_model.hint,
    needsTarget: true, targetLabel: "Select a model…", titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what should change — retrain on fresher data, add or drop features, predict something different…" },
];

const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";
const input = "w-full rounded-md border border-card-border bg-page-bg text-page-text text-sm px-3 py-2 outline-none focus:border-nav-active";
const label = "block text-xs uppercase tracking-wide text-page-text/60 mb-1";

const fmt = (v) => (v === null || v === undefined ? "—" : typeof v === "number" ? v.toFixed(3) : String(v));
const shortSha = (s) => (s ? String(s).slice(0, 10) : "—");

// The headline number. Cross-validated AUC when the engine reported it — a
// single holdout split is optimistic on a small dataset, and saying so is
// cheaper than being asked why the number moved.
function headlineMetric(metrics) {
  if (!metrics) return { label: "not evaluated", value: null, sub: null };
  const m = typeof metrics === "string" ? safeParse(metrics) : metrics;
  if (!m) return { label: "not evaluated", value: null, sub: null };
  if (m.cv_roc_auc_mean != null) {
    return {
      label: `ROC-AUC (${m.cv_folds || 5}-fold CV)`,
      value: m.cv_roc_auc_mean,
      sub: m.cv_roc_auc_std != null ? `± ${Number(m.cv_roc_auc_std).toFixed(3)}` : null,
    };
  }
  if (m.roc_auc != null) return { label: "ROC-AUC (holdout)", value: m.roc_auc, sub: "single split" };
  return { label: "accuracy", value: m.accuracy, sub: null };
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}
const asObj = (v) => (typeof v === "string" ? safeParse(v) : v) || null;

// Engine failures arrive as raw transport/RPC text (ECONNREFUSED to a service
// pod, HTTP 500 bodies, python tracebacks). Lead with a sentence the reader can
// act on and keep the original one click away — a wall of `connect ECONNREFUSED
// 172.20.x.x:80` tells a maintenance planner nothing about what to do next.
function friendlyError(raw) {
  const s = String(raw || "");
  if (/ECONNREFUSED|ENOTFOUND|socket hang up|EAI_AGAIN/i.test(s)) {
    return "The model engine was unreachable — it may still be starting up. Try again in a minute.";
  }
  if (/postgres connect failed|could not connect|password authentication/i.test(s)) {
    return "Could not connect to the project's database.";
  }
  if (/could not resolve the project postgres target|no postgres data_target/i.test(s)) {
    return "This project has no Postgres data target to train from.";
  }
  if (/training query failed|syntax error|does not exist/i.test(s)) {
    return "The training query failed — check the column and table names.";
  }
  if (/returned 0 rows/i.test(s)) return "The training query returned no rows.";
  if (/single class/i.test(s)) return "Every row has the same outcome, so there is nothing to learn.";
  if (/too few examples/i.test(s)) return "Too few examples of one outcome to train on.";
  if (/missing from query result|missing feature columns/i.test(s)) {
    return "The query is missing one of the feature columns.";
  }
  if (/timeout|timed out/i.test(s)) return "The run timed out.";
  // Unrecognised: show a trimmed first line rather than inventing a diagnosis.
  const first = s.split("\n")[0];
  return first.length > 140 ? `${first.slice(0, 140)}…` : first;
}

// The row-identifying column of a training query: its FIRST selected column,
// which by convention is the row key (a feature would appear in feature_spec
// instead). Handles `select x`, `select a.x`, and `select a.x as y` — and
// returns null rather than guessing when the shape is unexpected, so the caller
// can say so instead of scoring into the wrong key.
export function firstSelectedColumn(sql, featureSpec) {
  if (!sql || typeof sql !== "string") return null;
  const m = /^[\s(]*select\s+(?:distinct\s+)?([\s\S]*?)\s+from\s/i.exec(sql);
  if (!m) return null;
  // First top-level comma (ignore commas nested in function calls).
  const list = m[1];
  let depth = 0, end = list.length;
  for (let i = 0; i < list.length; i++) {
    const ch = list[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    else if (ch === "," && depth === 0) { end = i; break; }
  }
  const first = list.slice(0, end).trim();
  if (!first || first === "*") return null;
  // `expr as alias` → alias; otherwise the last dotted segment.
  const alias = /\s+as\s+("?[A-Za-z_][A-Za-z0-9_]*"?)\s*$/i.exec(first);
  const raw = alias ? alias[1] : first.split(/\s+/).pop();
  const name = String(raw).replace(/"/g, "").split(".").pop();
  if (!/^[A-Za-z_][A-Za-z0-9_]{0,62}$/.test(name)) return null;
  // A feature column is not a row id — that would be a mis-shaped query.
  const spec = featureSpec || {};
  const feats = [...(spec.numeric || []), ...(spec.categorical || [])];
  if (feats.includes(name)) return null;
  return name;
}

export default function Models() {
  const navigate = useNavigate();
  const { projectSlug, sharedSurfaceOnly } = useProject();
  const canBuild = !isPortal() && !sharedSurfaceOnly;

  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notProvisioned, setNotProvisioned] = useState(false);
  const [selected, setSelected] = useState(null);   // { model, versions, shares, can_write }
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);           // 'train' | 'score' | 'promote:<id>'
  const [flash, setFlash] = useState(null);         // { kind, text }
  const [menuModelId, setMenuModelId] = useState(null);
  const [requestDialog, setRequestDialog] = useState(null); // { kind, model? }
  const [openRequests, setOpenRequests] = useState(0);

  // Rail open/closed, persisted — same key convention as appsRailOpen /
  // agentsRailOpen so every explorer page behaves the same way.
  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("modelsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => {
    const n = !o;
    try { localStorage.setItem("modelsRailOpen", n ? "1" : "0"); } catch { /* private mode */ }
    return n;
  });

  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await listModels();
      setModels(r?.models || []);
      setNotProvisioned(false);
    } catch (e) {
      // A project whose backend predates this surface, or has no fork, should
      // read as "not set up here" rather than a red error.
      const msg = String(e?.message || e);
      if (/404|not found|Invalid table name/i.test(msg)) setNotProvisioned(true);
      else setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectSlug]);

  // Refetch the badge when the dialog opens/closes so a just-filed request
  // shows up immediately (the Agents variant of this effect).
  useEffect(() => {
    let alive = true;
    listAppRequests({ status: "open" })
      .then((r) => { if (alive) setOpenRequests((r?.requests || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [requestDialog]);

  useEffect(() => {
    if (menuModelId == null) return;
    const close = () => setMenuModelId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuModelId]);

  async function open(m) {
    try {
      const r = await getModel(m.id);
      setSelected(r);
    } catch (e) { setErr(String(e?.message || e)); }
  }

  async function refreshSelected(id) {
    try { setSelected(await getModel(id)); } catch { /* keep the stale view */ }
    load();
  }

  const displayName = (m) => m.display_name || m.name;
  const myModels = models.filter((m) => m.owner_email && m.is_owner !== false && !m.shared_with_me);
  const sharedModels = models.filter((m) => m.shared_with_me);
  const orgModels = models.filter((m) => !m.shared_with_me && m.is_owner === false);

  const Section = ({ label: secLabel, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{secLabel}</span>
      </div>
      {children}
    </div>
  );

  const ModelRow = ({ m }) => {
    const active = selected && selected.model.id === m.id;
    const menuOpen = menuModelId === m.id;
    const h = headlineMetric(m.active_metrics);
    return (
      <div
        data-testid={`model-${m.id}`}
        data-model-name={m.name}
        data-tour="model-row"
        className={
          "group relative w-full rounded-md text-sm " +
          (active ? "bg-nav-active text-white" : "text-page-text hover:bg-card-border/40")
        }
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`model-open-${m.id}`}
            onClick={() => open(m)}
            className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-inherit"
          >
            <span className="shrink-0 w-[16px] text-center leading-none" aria-hidden>{m.icon || "🧠"}</span>
            <span className="min-w-0 flex flex-col">
              <span className="flex items-center gap-1.5">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{displayName(m)}</span>
                {m.visibility === "org"
                  ? <Globe size={12} className="shrink-0 opacity-60" title="Visible to everyone in the organization" />
                  : <Lock size={12} className="shrink-0 opacity-60" title="Private" />}
              </span>
              {/* The headline number belongs in the row: it is the one fact that
                  decides whether a model is worth opening. */}
              <span className={"text-xs overflow-hidden text-ellipsis whitespace-nowrap " + (active ? "text-white/70" : "text-page-muted")}>
                {h.value == null
                  ? `predicts ${m.target_column || "—"} · not trained`
                  : `${h.label} ${Number(h.value).toFixed(3)} · ${m.version_count || 0} version${Number(m.version_count) === 1 ? "" : "s"}`}
              </span>
            </span>
          </button>
          {canBuild && (
            <button
              type="button"
              data-testid={`model-menu-${m.id}`}
              title="More"
              onClick={(e) => { e.stopPropagation(); setMenuModelId(menuOpen ? null : m.id); }}
              className={
                "shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent opacity-0 group-hover:opacity-100 " +
                (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-page-text") +
                (menuOpen ? " opacity-100" : "")
              }
            >
              <MoreVertical size={14} />
            </button>
          )}
        </div>
        {menuOpen && (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-52 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem icon={Wrench} label="Request a change" onClick={() => {
              setMenuModelId(null);
              setRequestDialog({ kind: "change_model", model: m });
            }} />
            <MenuItem icon={Play} label="Open & train" onClick={() => { setMenuModelId(null); open(m); }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0" data-tour="models-page">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="models-rail-open"
            onClick={toggleRail}
            title="Show models list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <Brain size={16} className="text-nav-active mt-1" />
        </div>
      )}

      {/* Left rail — the same explorer idiom as Apps and Agents. */}
      <aside data-tour="models-rail" className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <Brain size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Models</span>
          {canBuild && (
            <button
              type="button"
              data-testid="models-requests-badge"
              title="Open builds"
              className="ml-auto relative text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
              onClick={() => setRequestDialog({ kind: "new_model" })}
            >
              <ClipboardList size={15} />
              {openRequests > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-nav-active text-white text-[9px] leading-[14px] text-center">
                  {openRequests}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            data-testid="models-refresh"
            data-tour="models-refresh"
            onClick={load}
            title="Refresh"
            className={(canBuild ? "" : "ml-auto ") + "text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            type="button"
            data-testid="models-rail-close"
            onClick={toggleRail}
            title="Collapse models list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2" data-tour="models-list">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading models…
            </div>
          )}
          {!loading && err && <div className="text-sm text-red-500 px-2 py-3">{err}</div>}
          {!loading && notProvisioned && (
            <div className="text-sm text-page-muted px-2 py-3">
              Models are not available for this project yet.
            </div>
          )}
          {!loading && !err && !notProvisioned && models.length === 0 && (
            <div className="text-sm text-page-muted px-2 py-3" data-tour="models-empty">
              No models yet in this project. Build one below.
            </div>
          )}
          {!loading && !err && myModels.length > 0 && (
            <Section label="My models">{myModels.map((m) => <ModelRow key={m.id} m={m} />)}</Section>
          )}
          {!loading && !err && sharedModels.length > 0 && (
            <Section label="Shared with me">{sharedModels.map((m) => <ModelRow key={m.id} m={m} />)}</Section>
          )}
          {!loading && !err && orgModels.length > 0 && (
            <Section label="Organization">{orgModels.map((m) => <ModelRow key={m.id} m={m} />)}</Section>
          )}
        </div>

        {canBuild && (
          <div className="p-2 border-t border-card-border grid gap-2">
            <button
              type="button"
              data-testid="models-request-new"
              onClick={() => setRequestDialog({ kind: "new_model" })}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
            >
              <Sparkles size={14} /> Build a model
            </button>
            {/* Hand-rolled creation stays available for anyone who already knows
                their query — the request route is for "I want a model that…". */}
            <button
              type="button"
              data-tour="models-new"
              data-testid="models-new"
              onClick={() => setCreating(true)}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-1.5 border-0 bg-transparent text-page-muted hover:text-page-text cursor-pointer"
            >
              <Plus size={14} /> New model, manually
            </button>
          </div>
        )}
      </aside>

      {/* Detail pane */}
      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg overflow-y-auto">
        {flash && (
          <div
            className={`sticky top-0 z-30 mx-4 mt-4 shadow-lg rounded-md px-3 py-2 text-sm flex items-start gap-2 ${
              flash.kind === "error"
                ? "bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border border-red-500/40"
                : "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40"
            }`}
            role="status"
            aria-live="polite"
            data-tour="models-flash"
          >
            {flash.kind === "error"
              ? <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              : <CheckCircle2 size={15} className="mt-0.5 shrink-0" />}
            <span className="flex-1 whitespace-pre-wrap">{flash.text}</span>
            <button className="opacity-60 hover:opacity-100 shrink-0" onClick={() => setFlash(null)} aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        {!selected && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Brain size={40} className="text-page-muted mb-3" />
            <div className="text-page-text font-medium">Your project models</div>
            <div className="text-sm text-page-muted mt-1 max-w-md">
              Pick a model from the left to see what it predicts, how well it scores, which
              data commit trained it, and which features drive its predictions. A model learns
              from a query over this project&apos;s governed tables.
            </div>
            {canBuild && (
              <button
                type="button"
                data-testid="models-request-new-main"
                onClick={() => setRequestDialog({ kind: "new_model" })}
                className={btnGhost + " mt-4"}
              >
                <Sparkles size={14} /> Build a model
              </button>
            )}
          </div>
        )}

        {selected && (
          <ModelDetail
            data={selected}
            busy={busy}
            setBusy={setBusy}
            onClose={() => setSelected(null)}
            onFlash={setFlash}
            onChanged={() => refreshSelected(selected.model.id)}
            onRemoved={async () => { setSelected(null); await load(); }}
            onRequestChange={(m) => setRequestDialog({ kind: "change_model", model: m })}
          />
        )}
      </main>

      {creating && (
        <CreateModelDialog
          onClose={() => setCreating(false)}
          onCreated={async (m) => {
            setCreating(false);
            setFlash({ kind: "ok", text: `Model “${m.display_name || m.name}” created. Train it to produce a first version.` });
            await load();
            open(m);
          }}
          onError={(msg) => setFlash({ kind: "error", text: msg })}
        />
      )}

      {requestDialog && (
        <AppRequestDialog
          kinds={MODEL_REQUEST_KINDS}
          targets={models.map((m) => ({ id: m.id, label: displayName(m) }))}
          targetField="model_id"
          title="Build a model"
          preset={{
            kind: requestDialog.kind,
            title: requestDialog.model ? `Change model ${displayName(requestDialog.model)}` : "",
            targetId: requestDialog.model?.id ?? null,
          }}
          onClose={() => setRequestDialog(null)}
          onCreated={() => { setRequestDialog(null); navigate(createPageUrl("board")); }}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label: itemLabel, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent " +
        (danger ? "text-red-500 hover:bg-red-500/10" : "text-page-text hover:bg-card-border/40")
      }
    >
      <Icon size={14} className="shrink-0 opacity-80" /> {itemLabel}
    </button>
  );
}

function CreateModelDialog({ onClose, onCreated, onError }) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [targetColumn, setTargetColumn] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const slug = useMemo(
    () => name.toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 128),
    [name]
  );

  async function submit(e) {
    e.preventDefault();
    if (!slug) return;
    setSaving(true);
    try {
      const r = await createModel({
        name: slug,
        display_name: displayName || name,
        description,
        target_column: targetColumn || null,
        icon: "🧠",
        task: "classification",
      });
      onCreated(r.model);
    } catch (err) {
      onError(`Could not create the model: ${String(err?.message || err)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New model" onClose={onClose} tour="models-create-dialog">
      <form onSubmit={submit} className="grid gap-3">
        <div>
          <label className={label} htmlFor="m-name">Name</label>
          <input id="m-name" className={input} value={name} autoFocus
                 onChange={(e) => setName(e.target.value)}
                 placeholder="sensor anomaly" data-tour="models-create-name" />
          {slug && <div className="text-[11px] text-page-text/50 mt-1 font-mono">id: {slug}</div>}
        </div>
        <div>
          <label className={label} htmlFor="m-display">Display name</label>
          <input id="m-display" className={input} value={displayName}
                 onChange={(e) => setDisplayName(e.target.value)} placeholder="Sensor anomaly" />
        </div>
        <div>
          <label className={label} htmlFor="m-target">Column to predict</label>
          <input id="m-target" className={input} value={targetColumn}
                 onChange={(e) => setTargetColumn(e.target.value)} placeholder="abnormal"
                 data-tour="models-create-target" />
          <div className="text-[11px] text-page-text/50 mt-1">
            The 0/1 column your training query produces.
          </div>
        </div>
        <div>
          <label className={label} htmlFor="m-desc">Description</label>
          <textarea id="m-desc" className={input} rows={2} value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What this model is for, and how its output should be used." />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" className={btnPrimary} disabled={!slug || saving} data-tour="models-create-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── detail ─────────────────────────────────────────────────────────────────
// Three tabs under one shared header (identity + EDITABLE description):
//   Test run (default)      — the runner: input contract, output, timings, dependants
//   Versions & training     — train, promote, metrics, lineage, importances
//   Algorithm               — how it works, read live from the Flow fork manifest
// Plus "Ask the assistant": opens the project chat switched to the stamped
// Models Assistant, seeded with this model's name — the agent has
// get_model/train/predict/score_all, so it can answer anything about it.
function ModelDetail({ data, busy, setBusy, onClose, onFlash, onChanged, onRemoved, onRequestChange }) {
  const { model, versions, can_write: canWrite } = data;
  const [showTrain, setShowTrain] = useState(false);
  const [tab, setTab] = useState("run");
  const active = versions.find((v) => v.id === model.active_version_id) || null;

  // Reset to the default tab when switching models — carrying the Algorithm
  // tab from one model to another reads as a rendering glitch.
  useEffect(() => { setTab("run"); }, [model.id]);

  function askAssistant() {
    window.dispatchEvent(new CustomEvent("cortex:open-harness", {
      detail: {
        harness: "orchestrator",
        selectedAgent: { name: "models", display_name: "Models Assistant", icon: "🧠" },
        initialPrompt:
          `Tell me about the model "${model.name}" in this project — what it predicts, ` +
          `how it works, how trustworthy its current score is, and what drives its predictions.`,
      },
    }));
  }

  async function promote(v) {
    setBusy(`promote:${v.id}`);
    try {
      await promoteVersion(model.id, v.id);
      onFlash({ kind: "ok", text: `Version ${v.version} is now active.` });
      onChanged();
    } catch (e) {
      onFlash({ kind: "error", text: `Could not promote version ${v.version}: ${String(e?.message || e)}` });
    } finally { setBusy(null); }
  }

  async function remove() {
    setBusy("remove");
    try {
      await removeModel(model.id);
      onFlash({ kind: "ok", text: `Model “${model.display_name || model.name}” removed.` });
      onRemoved();
    } catch (e) {
      onFlash({ kind: "error", text: String(e?.message || e) });
    } finally { setBusy(null); }
  }

  async function toggleVisibility() {
    setBusy("vis");
    try {
      await updateModelVisibility(model.id, model.visibility === "private" ? "org" : "private");
      onChanged();
    } catch (e) {
      onFlash({ kind: "error", text: String(e?.message || e) });
    } finally { setBusy(null); }
  }

  const TABS = [
    { id: "run", label: "Test run", icon: FlaskConical },
    { id: "train", label: "Versions & training", icon: Layers },
    { id: "algorithm", label: "Algorithm", icon: Cpu },
  ];

  return (
    <div data-tour="model-detail" className="p-5">
      {/* Pane header — mirrors the Apps detail bar: title, owner, actions. */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-lg" aria-hidden>{model.icon || "🧠"}</span>
        <span className="font-semibold text-page-text truncate">{model.display_name || model.name}</span>
        <button className={btnGhost + " ml-auto text-xs"} onClick={askAssistant}
                data-tour="model-ask-assistant"
                title="Chat with the Models Assistant about this model">
          <MessageCircle size={13} /> Ask the assistant
        </button>
        {canWrite && onRequestChange && (
          <button className={btnGhost + " text-xs"} onClick={() => onRequestChange(model)}
                  data-testid="model-request-change">
            <Wrench size={13} /> Request a change
          </button>
        )}
        <button className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
                onClick={onClose} title="Close" aria-label="Close">
          <X size={16} />
        </button>
      </div>

      {/* Shared above BOTH tabs: identity facts + the editable description. */}
      <div className="grid gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-page-text/60">
          <code className="font-mono">{model.name}</code>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Target size={11} /> {model.target_column || "—"}</span>
          <span>·</span>
          <span>{model.task}</span>
          {active && (
            <>
              <span>·</span>
              <span>active v{active.version}</span>
            </>
          )}
          {canWrite && (
            <>
              <span>·</span>
              <button className="inline-flex items-center gap-1 underline decoration-dotted hover:text-page-text"
                      onClick={toggleVisibility} disabled={busy === "vis"} data-tour="model-visibility">
                {model.visibility === "private" ? <Lock size={11} /> : <Globe size={11} />}
                {model.visibility === "private" ? "Private" : "Organization"}
              </button>
            </>
          )}
        </div>
        <EditableDescription model={model} canWrite={canWrite} onFlash={onFlash} onChanged={onChanged} />
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-card-border mb-4" role="tablist" data-tour="model-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                  data-tour={`model-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={
                    "inline-flex items-center gap-1.5 px-3 py-2 text-sm border-0 border-b-2 bg-transparent cursor-pointer -mb-px " +
                    (tab === t.id
                      ? "border-nav-active text-page-text font-medium"
                      : "border-transparent text-page-muted hover:text-page-text")
                  }
                  style={{ borderBottomStyle: "solid" }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
        {/* the runner stays deep-linkable at its own URL */}
        {tab === "run" && (
          <a href={createPageUrl(`models/${model.name}/run`)} target="_blank" rel="noreferrer"
             className="ml-auto text-page-muted hover:text-page-text p-1.5" title="Open the runner in its own tab"
             data-tour="model-open-runner">
            <ExternalLink size={13} />
          </a>
        )}
      </div>

      {tab === "run" && <ModelRunner model={model} versions={versions} />}

      {tab === "algorithm" && <AlgorithmTab model={model} versions={versions} />}

      {tab === "train" && (
      <div className="grid gap-5">
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <button className={btnPrimary} onClick={() => setShowTrain(true)} data-tour="model-train-open">
              <Play size={14} /> Train a version
            </button>
            <button className={btnGhost} onClick={remove} disabled={busy === "remove"} data-tour="model-remove">
              <Trash2 size={14} /> Remove
            </button>
          </div>
        )}

        {showTrain && (
          <TrainPanel
            model={model}
            onClose={() => setShowTrain(false)}
            onFlash={onFlash}
            onDone={() => { setShowTrain(false); onChanged(); }}
            busy={busy}
            setBusy={setBusy}
          />
        )}

        {active && <ActiveVersionCard version={active} model={model} onFlash={onFlash} busy={busy} setBusy={setBusy} canWrite={canWrite} />}

        <section data-tour="model-versions">
          <h3 className="text-sm font-semibold text-page-text mb-2 flex items-center gap-1.5">
            <Layers size={14} /> Versions
          </h3>
          {versions.length === 0 && (
            <div className="text-sm text-page-text/60">
              No versions yet — train one to get a scored, explainable model.
            </div>
          )}
          <div className="grid gap-2">
            {versions.map((v) => {
              const h = headlineMetric(v.metrics);
              const isActive = v.id === model.active_version_id;
              return (
                <div key={v.id}
                     className={`rounded-md border p-3 grid gap-2 md:grid-cols-[1fr_auto] items-center ${
                       isActive ? "border-nav-active/60 bg-nav-active/5" : "border-card-border bg-card-bg"}`}
                     data-version={v.version}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-page-text">v{v.version}</span>
                      <StatusPill status={v.status} />
                      {isActive && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-nav-active text-white">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-page-text/60 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1" title={`Recipe commit ${v.dolt_commit}`}>
                        <GitCommitHorizontal size={11} /> <code className="font-mono">{shortSha(v.dolt_commit)}</code>
                      </span>
                      {v.row_count != null && (
                        <span className="inline-flex items-center gap-1">
                          <Database size={11} /> {v.row_count} rows
                          {v.positive_count != null && ` · ${v.positive_count} positive`}
                        </span>
                      )}
                      {v.trained_by && <span className="text-page-text/40">{v.trained_by}</span>}
                    </div>
                    {v.status === "failed" && v.error && (
                      <details className="mt-1">
                        <summary className="text-xs text-red-500 cursor-pointer list-none">
                          {friendlyError(v.error)}
                          <span className="text-page-text/40 ml-1 underline decoration-dotted">details</span>
                        </summary>
                        <div className="text-[11px] text-page-text/60 mt-1 whitespace-pre-wrap break-words font-mono">
                          {v.error}
                        </div>
                      </details>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-page-text/50">{h.label}</div>
                      <div className="text-base font-semibold tabular-nums text-page-text">
                        {h.value == null ? "—" : Number(h.value).toFixed(3)}
                      </div>
                    </div>
                    {canWrite && !isActive && v.status === "trained" && (
                      <button className={btnGhost} onClick={() => promote(v)}
                              disabled={busy === `promote:${v.id}`} data-tour="model-promote">
                        {busy === `promote:${v.id}` ? <Loader2 size={13} className="animate-spin" /> : <TrendingUp size={13} />}
                        Promote
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Two histories, deliberately separate: the versions above are TRAINING
            runs (each pinned to the recipe's Dolt commit), while this is the
            history of the model's DEFINITION — the manifest endpoint on the fork,
            versioned by Flow's git like every app and agent. */}
        <section>
          <VersionHistory kind="model" name={model.name} title="Definition history"
                          canRestore={canWrite} />
        </section>
      </div>
      )}
    </div>
  );
}

// Click-to-edit description, shared above every tab. Saving republishes the
// manifest server-side — the description is part of what the model IS, so the
// fork (and both assistants) must see the new text, not just the mirror.
function EditableDescription({ model, canWrite, onFlash, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(model.description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(model.description || ""); setEditing(false); }, [model.id, model.description]);

  async function save() {
    setSaving(true);
    try {
      await updateModelDescription(model.id, text.trim());
      setEditing(false);
      onChanged();
    } catch (e) {
      onFlash({ kind: "error", text: `Could not save the description: ${String(e?.message || e)}` });
    } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <div className="grid gap-2" data-tour="model-description-edit">
        <textarea className={input} rows={3} value={text} autoFocus
                  onChange={(e) => setText(e.target.value)}
                  placeholder="What this model is for, and how its output should be used." />
        <div className="flex gap-2">
          <button className={btnPrimary + " text-xs"} onClick={save} disabled={saving}
                  data-tour="model-description-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save
          </button>
          <button className={btnGhost + " text-xs"} disabled={saving}
                  onClick={() => { setText(model.description || ""); setEditing(false); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="group flex items-start gap-2" data-tour="model-description">
      <p className={"text-sm m-0 " + (model.description ? "text-page-text/80" : "text-page-text/40 italic")}>
        {model.description || (canWrite ? "No description yet — say what this model is for and how its output should be used." : "No description.")}
      </p>
      {canWrite && (
        <button type="button" title="Edit description" data-tour="model-description-editbtn"
                onClick={() => setEditing(true)}
                className="shrink-0 p-1 rounded bg-transparent border-0 cursor-pointer text-page-muted opacity-0 group-hover:opacity-100 hover:text-page-text">
          <Pencil size={13} />
        </button>
      )}
    </div>
  );
}

// ── algorithm — read live from the Flow fork ────────────────────────────────
// "How does it actually work?" answered from the authority: the model's
// manifest endpoint on the paired Flow fork (Mongo-authoritative, git-versioned)
// plus the fixed engine pipeline the `ml` service runs. Nothing here is typed
// by hand into the page — the manifest is fetched live, so if the fork and the
// mirror ever disagree, this tab shows what the engine will actually use.
const FRAMEWORKS = {
  "sklearn-hgb": {
    name: "Gradient-boosted decision trees (scikit-learn HistGradientBoostingClassifier)",
    steps: [
      "The training query runs against the project's governed Postgres — its first column is the row id, the rest are features plus the 0/1 target column.",
      "Numeric features: missing values are imputed; category features: one-hot encoded. No manual feature scaling is needed — trees are scale-invariant.",
      "A HistGradientBoostingClassifier is fitted: an ensemble of shallow decision trees, each correcting the errors of the ones before it.",
      "A quarter of the rows is held back for the holdout metrics, and a 5-fold cross-validation produces the headline ROC-AUC — the figure to trust.",
      "Global feature importances are computed and stored with the version, so a dominant feature (possible leakage) is visible at a glance.",
      "The fitted pipeline is serialized to S3 with a SHA-256 fingerprint, and the version records the Dolt commit of the recipe that produced its training data.",
    ],
  },
};

function AlgorithmTab({ model, versions }) {
  const [manifest, setManifest] = useState({ loading: true, error: null, data: null, endpoint: null });
  const [showJson, setShowJson] = useState(false);
  const active = versions.find((v) => v.id === model.active_version_id) || null;
  const fw = FRAMEWORKS[active?.framework] || null;
  const spec = asObj(active?.feature_spec) || {};

  useEffect(() => {
    let gone = false;
    getModelManifest(model.id)
      .then((r) => { if (!gone) setManifest({ loading: false, error: null, data: r?.manifest, endpoint: r?.endpoint }); })
      .catch((e) => { if (!gone) setManifest({ loading: false, error: String(e?.message || e), data: null, endpoint: null }); });
    return () => { gone = true; };
  }, [model.id]);

  return (
    <div className="grid gap-4" data-tour="model-algorithm">
      <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-3">
        <h3 className="text-sm font-semibold text-page-text m-0 flex items-center gap-1.5">
          <Cpu size={14} /> {fw ? fw.name : active?.framework ? `Framework: ${active.framework}` : "Not trained yet"}
        </h3>
        {fw && (
          <ol className="m-0 pl-5 grid gap-1.5 text-sm text-page-text/80" data-tour="model-algorithm-steps">
            {fw.steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        )}
        {!fw && !active && (
          <div className="text-sm text-page-text/60">
            Train a version first — the algorithm description comes from the trained artifact.
          </div>
        )}
        <div className="text-xs text-page-text/50">
          Training and scoring run on the shared <code className="font-mono">ml</code> engine service —
          endpoints <code className="font-mono">cortex.ml.train</code>, <code className="font-mono">cortex.ml.predict</code> and{" "}
          <code className="font-mono">cortex.strategy.ml_predict.apply</code> — invoked with this project&apos;s
          own database and storage context. The same input always produces the same output until a new
          version is deliberately promoted.
        </div>
      </section>

      {active && (
        <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-3">
          <h3 className="text-sm font-semibold text-page-text m-0">Active version — what v{active.version} learned from</h3>
          <div className="grid gap-1 text-xs text-page-text/70">
            {(spec.numeric?.length || 0) > 0 && (
              <div>Numeric features: {spec.numeric.map((c) => <code key={c} className="font-mono mr-1.5">{c}</code>)}</div>
            )}
            {(spec.categorical?.length || 0) > 0 && (
              <div>Category features: {spec.categorical.map((c) => <code key={c} className="font-mono mr-1.5">{c}</code>)}</div>
            )}
            <div>Predicts: <code className="font-mono">{model.target_column || "—"}</code></div>
            <div className="flex items-center gap-1.5">
              <GitCommitHorizontal size={12} /> Training data from recipe commit{" "}
              <code className="font-mono">{active.dolt_commit}</code>
            </div>
          </div>
          {active.training_sql && (
            <div>
              <div className={label}>Training query (the dataset definition)</div>
              <pre className="m-0 rounded-md border border-card-border bg-page-bg p-3 text-[11px] font-mono whitespace-pre-wrap text-page-text/80 overflow-x-auto"
                   data-tour="model-algorithm-sql">{active.training_sql}</pre>
            </div>
          )}
        </section>
      )}

      <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-2" data-tour="model-algorithm-manifest">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-page-text m-0">Definition on the Flow fork (live)</h3>
          {manifest.loading && <Loader2 size={13} className="animate-spin text-page-muted" />}
        </div>
        <div className="text-xs text-page-text/60">
          The model&apos;s authoritative definition is the{" "}
          <code className="font-mono">{manifest.endpoint || `cortex.model.${model.name}.manifest`}</code> endpoint
          on this project&apos;s Flow fork — versioned by the fork&apos;s git (see Definition history on the
          Versions &amp; training tab). This is what the engine, workflows and assistants read.
        </div>
        {manifest.error && <div className="text-xs text-red-500">Could not read the fork manifest: {manifest.error}</div>}
        {manifest.data && (
          <div>
            <button type="button" className={btnGhost + " text-xs"} onClick={() => setShowJson((s) => !s)}
                    data-tour="model-algorithm-json-toggle">
              {showJson ? "Hide" : "Show"} manifest JSON
            </button>
            {showJson && (
              <pre className="mt-2 m-0 rounded-md border border-card-border bg-page-bg p-3 text-[11px] font-mono whitespace-pre-wrap text-page-text/80 overflow-x-auto max-h-96 overflow-y-auto"
                   data-tour="model-algorithm-json">{JSON.stringify(manifest.data, null, 2)}</pre>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    trained: "bg-emerald-500/15 text-emerald-600",
    training: "bg-amber-500/15 text-amber-600",
    failed: "bg-red-500/15 text-red-500",
    archived: "bg-page-text/10 text-page-text/60",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${map[status] || map.archived}`}>
      {status}
    </span>
  );
}

// The active version gets the full treatment: every metric, the lineage pin,
// and the importances — which are the part that makes a score actionable.
function ActiveVersionCard({ version, model, onFlash, busy, setBusy, canWrite }) {
  const m = asObj(version.metrics) || {};
  const imps = asObj(version.importances) || [];
  const spec = asObj(version.feature_spec) || {};
  const top = imps[0];

  // Which column identifies a scored row. The training query's FIRST selected
  // column is the convention (it is the row key by construction — it is not a
  // feature, or it would be listed in feature_spec). Derived rather than
  // hardcoded so this page stays generic across projects.
  const idColumn = useMemo(
    () => firstSelectedColumn(version.training_sql, spec),
    [version.training_sql, version.id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  async function runBatch() {
    if (!idColumn) {
      onFlash({
        kind: "error",
        text: "Could not tell which column identifies a row. Make the first column of the training query the row id (e.g. reading_id).",
      });
      return;
    }
    setBusy("score");
    try {
      const r = await scoreBatch({
        model_id: model.id,
        artifact_key: version.artifact_key,
        artifact_bucket: version.artifact_bucket,
        sql: version.training_sql,
        id_column: idColumn,
        model_name: model.name,
        model_version: String(version.version),
        output_table: "ml_prediction",
      });
      onFlash({
        kind: "ok",
        text: `Scored ${r.scored} rows into ${r.output_table} — ${r.flagged} flagged at threshold ${r.threshold}.`,
      });
    } catch (e) {
      onFlash({ kind: "error", text: `Batch scoring failed. ${friendlyError(e?.message || e)}` });
    } finally { setBusy(null); }
  }

  return (
    <section className="rounded-lg border border-nav-active/40 bg-nav-active/5 p-4" data-tour="model-active">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-page-text m-0">
          Active version — v{version.version}
        </h3>
        {canWrite && version.artifact_key && (
          <button className={btnGhost} onClick={runBatch} disabled={busy === "score"} data-tour="model-score">
            {busy === "score" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Score all rows
          </button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <div className={label}>Metrics</div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm m-0">
            {[
              ["CV ROC-AUC", m.cv_roc_auc_mean != null ? `${fmt(m.cv_roc_auc_mean)} ± ${fmt(m.cv_roc_auc_std)}` : null],
              ["Holdout ROC-AUC", m.roc_auc],
              ["Avg precision", m.average_precision],
              ["Precision", m.precision],
              ["Recall", m.recall],
              ["F1", m.f1],
              ["Train / test", m.n_train != null ? `${m.n_train} / ${m.n_test}` : null],
              ["Positive rate", m.positive_rate],
            ].filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-page-text/60 text-xs self-center">{k}</dt>
                <dd className="m-0 tabular-nums text-page-text">{typeof v === "number" ? fmt(v) : v}</dd>
              </React.Fragment>
            ))}
          </dl>
          {m.cv_roc_auc_mean != null && m.roc_auc != null && Number(m.roc_auc) > Number(m.cv_roc_auc_mean) + 0.02 && (
            <div className="text-[11px] text-page-text/60 mt-2">
              The holdout score is higher than the cross-validated one — trust the CV figure.
            </div>
          )}
        </div>

        <div>
          <div className={label}>What drives the prediction</div>
          {imps.length === 0 && <div className="text-sm text-page-text/60">Not available.</div>}
          <div className="grid gap-1.5" data-tour="model-importances">
            {imps.slice(0, 6).map((i) => (
              <div key={i.feature} className="grid grid-cols-[9rem_1fr_3rem] items-center gap-2">
                <code className="text-[11px] font-mono text-page-text/80 truncate" title={i.feature}>
                  {i.feature}
                </code>
                <div className="h-2 rounded bg-page-text/10 overflow-hidden">
                  <div className="h-full bg-nav-active"
                       style={{ width: `${Math.round((i.importance || 0) * 100)}%` }} />
                </div>
                <span className="text-[11px] tabular-nums text-page-text/70 text-right">
                  {Math.round((i.importance || 0) * 100)}%
                </span>
              </div>
            ))}
          </div>
          {top && top.importance >= 0.6 && (
            <div className="text-[11px] text-amber-600 mt-2 flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>
                <code className="font-mono">{top.feature}</code> alone drives{" "}
                {Math.round(top.importance * 100)}% of the prediction. Check that it is genuinely
                available before the outcome is known.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-card-border grid gap-1 text-xs text-page-text/60">
        <div className="flex items-center gap-1.5">
          <GitCommitHorizontal size={12} />
          <span>Trained from recipe commit</span>
          <code className="font-mono text-page-text/80">{version.dolt_commit}</code>
        </div>
        {(spec.numeric?.length || spec.categorical?.length) && (
          <div>
            Features: {[...(spec.numeric || []), ...(spec.categorical || [])].join(", ")}
          </div>
        )}
        {version.artifact_sha256 && (
          <div>Artifact <code className="font-mono">{version.artifact_sha256.slice(0, 16)}…</code></div>
        )}
      </div>
    </section>
  );
}

// ── train ──────────────────────────────────────────────────────────────────
const DEFAULT_NUMERIC = "reading_value, baseline_value, ratio, health_score, operating_hours";
const DEFAULT_CATEGORICAL = "sensor_type, criticality";

function TrainPanel({ model, onClose, onFlash, onDone, busy, setBusy }) {
  const [sql, setSql] = useState("");
  const [target, setTarget] = useState(model.target_column || "");
  const [numeric, setNumeric] = useState(DEFAULT_NUMERIC);
  const [categorical, setCategorical] = useState(DEFAULT_CATEGORICAL);

  const parseCols = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  async function run(e) {
    e.preventDefault();
    setBusy("train");
    try {
      const r = await trainModel({
        model_id: model.id,
        sql: sql.trim(),
        target_column: target.trim(),
        feature_spec: { numeric: parseCols(numeric), categorical: parseCols(categorical) },
      });
      const h = headlineMetric(r?.version?.metrics);
      onFlash({
        kind: "ok",
        text: `Version ${r?.version?.version} trained on ${r?.version?.row_count} rows — ${h.label} ${
          h.value == null ? "n/a" : Number(h.value).toFixed(3)}${r?.auto_promoted ? " (now active)" : ""}.`,
      });
      onDone();
    } catch (err) {
      onFlash({ kind: "error", text: `Training failed. ${friendlyError(err?.message || err)}` });
    } finally { setBusy(null); }
  }

  return (
    <form onSubmit={run} className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-3"
          data-tour="model-train-panel">
      <div className="text-sm font-semibold text-page-text">Train a new version</div>
      <div>
        <label className={label} htmlFor="t-sql">Training query (SELECT only)</label>
        <textarea id="t-sql" className={`${input} font-mono text-xs`} rows={6} value={sql}
                  onChange={(e) => setSql(e.target.value)} spellCheck={false}
                  data-tour="model-train-sql"
                  placeholder={`select r.reading_id, r.reading_value, r.baseline_value,\n  (r.reading_value/nullif(r.baseline_value,0)) as ratio,\n  r.sensor_type, a.criticality, a.health_score, a.operating_hours,\n  case when r.reading_status in ('warning','alarm') then 1 else 0 end as abnormal\nfrom sensor_readings r left join assets a on a.asset_id = r.reading_asset_id`} />
        <div className="text-[11px] text-page-text/50 mt-1">
          Runs against this project&apos;s governed database. It must return the column to
          predict plus every feature column.
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <label className={label} htmlFor="t-target">Column to predict</label>
          <input id="t-target" className={input} value={target}
                 onChange={(e) => setTarget(e.target.value)} placeholder="abnormal"
                 data-tour="model-train-target" />
        </div>
        <div>
          <label className={label} htmlFor="t-num">Numeric features</label>
          <input id="t-num" className={input} value={numeric} onChange={(e) => setNumeric(e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor="t-cat">Category features</label>
          <input id="t-cat" className={input} value={categorical} onChange={(e) => setCategorical(e.target.value)} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="submit" className={btnPrimary}
                disabled={busy === "train" || !sql.trim() || !target.trim()}
                data-tour="model-train-submit">
          {busy === "train" ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {busy === "train" ? "Training…" : "Train"}
        </button>
      </div>
    </form>
  );
}

// ── modal shell ────────────────────────────────────────────────────────────
function Modal({ title, children, onClose, wide, tour }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 md:p-8"
         onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className={`bg-page-bg border border-card-border rounded-lg shadow-xl w-full ${wide ? "max-w-[880px]" : "max-w-[520px]"}`}
           onClick={(e) => e.stopPropagation()} data-tour={tour}>
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-card-border">
          <h2 className="text-base font-semibold text-page-text m-0">{title}</h2>
          <button onClick={onClose} className="text-page-text/60 hover:text-page-text" aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
